/*
  Page Title Patterns
  ------------------
  Base pages (index.tsx):    "{Base}" (e.g., "Staff", "Cash Management")
  New transaction pages:     "New {Base}" (e.g., "New Sale") 
  Detail pages ([id].tsx):   "{Base} details" (e.g., "Product details")
  Tab/filtered pages:        "{Base} › {Tab}" (e.g., "Sales › Drafts")

  Notes:
    - Use title case for base and new transaction pages
    - Use sentence case for detail and tab/filtered pages
    - After the › symbol, capitalize the next word as if starting a new sentence
    - Use › (U+203A), not > (U+003E) for navigation breadcrumbs
*/

// Disallow > (U+003E) in any string literal
type NoGt<S extends string> = S extends `${string}>${string}` ? never : S

// Identity helper that keeps literal types intact
const titles = <const T extends { [K in keyof T]: NoGt<T[K]> }>(obj: T) => obj

export const POSPageTitles = titles({
  /// Transactions
  NEW_SALE: "New Sale",
  SALE_DETAILS: "Sale details",
  SALE_RETURN_DETAILS: "Return details",
  SALES_DRAFTS: "Transactions › Drafts",
  SALES_QUOTES: "Transactions › Quotes",
  SALES_WILL_CALL: "Transactions › Will call",
  SALES_ONLINE_ORDERS: "Transactions › Online orders",
  SALES_SPECIAL_ORDERS: "Transactions › Special orders",
  SALES_SOLD: "Transactions › Sold",
  SALES_VOIDED: "Transactions › Voided",

  /// Receivables
  RECEIVABLES_PAYMENT: "New Bill Payment", // special
  RECEIVABLES_DETAILS: "Receivable details",
  RECEIVABLES_PAID: "Receivables › Paid",
  RECEIVABLES_REFUNDED: "Receivables › Refunded",
  RECEIVABLES_FINANCE_CHARGES: "Receivables › Finance charges",
  RECEIVABLES_FINANCE_CHARGE_DETAILS: "Finance charge details",
  RECEIVABLES_CREDITS: "Receivables › Credits",
  RECEIVABLES_CREDIT_DETAILS: "Credit details",

  /// Cash Management
  CASH_MANAGEMENT: "Cash Management",

  /// Deliveries
  DELIVERY_DETAILS: "Delivery details",
  DELIVERIES_CALENDAR: "Deliveries › Calendar",
  DELIVERIES_LIST: "Deliveries › List",

  /// Purchase Orders
  PURCHASE_ORDERS_NEW: "New Order",
  PURCHASE_ORDERS_DETAILS: "Order details",
  PURCHASE_ORDERS_DRAFTS: "Orders › Drafts",
  PURCHASE_ORDERS_ORDERED: "Orders › Ordered",
  PURCHASE_ORDERS_RECEIVED: "Orders › Received",
  PURCHASE_ORDERS_VOUCHERED: "Orders › Vouchered",
  PURCHASE_ORDERS_PAID: "Orders › Paid",
  PURCHASE_ORDERS_VOIDED: "Orders › Voided",
  PURCHASE_ORDERS_BUYERS_LIST: "Orders › Buyers list",

  /// Transfers
  TRANSFERS_NEW: "New Transfer",
  TRANSFERS_DETAILS: "Transfer details",
  TRANSFERS_DRAFTS: "Transfers › Drafts",
  TRANSFERS_REQUESTED: "Transfers › Requested",
  TRANSFERS_SENT: "Transfers › Sent",
  TRANSFERS_RECEIVED: "Transfers › Received",
  TRANSFERS_VOIDED: "Transfers › Voided",

  /// Counts
  COUNTS_DETAILS: "Count details",
  COUNTS_IN_PROGRESS: "Counts › In progress",
  COUNTS_COMPLETED: "Counts › Completed",
  COUNTS_VOIDED: "Counts › Voided",

  /// Products
  NEW_CUSTOM_PRICE: "New custom price",
  CUSTOM_PRICE_DETAILS: "Custom price details",
  PRODUCTS: "Products",
  PRODUCT_DETAILS: "Product details",
  PRODUCTS_CUSTOM_PRICES: "Products › Custom prices",
  PRODUCTS_PROMOTIONS: "Products › Promotions",
  PRODUCTS_SPIFFS: "Products › Spiffs",
  PRODUCTS_PRICE_CHANGES: "Products › Price changes",
  PRODUCTS_CATALOG: "Products › Catalog",
  PRODUCTS_INTEGRATIONS: "Products › Integrations",
  PRODUCTS_GIFT_CARDS: "Products › Gift cards",
  PRODUCTS_GIFT_CARD_DETAILS: "Gift card details",
  NEW_PROMOTION: "New promotion",
  PROMOTION_DETAILS: "Promotion details",

  /// Customers
  CUSTOMERS: "Customers",
  CUSTOMER_TRANSACTIONS: "Customer › Transactions",
  CUSTOMER_BALANCE: "Customer › Balance",
  CUSTOMER_JOBS: "Customer › Jobs",
  CUSTOMER_RELATIONSHIP: "Customer › Relationship",
  CUSTOMER_CUSTOM_PRICES: "Customer › Custom prices",
  CUSTOMER_LOYALTY: "Customer › Loyalty",
  CUSTOMER_SETTINGS: "Customer › Settings",
  CUSTOMERS_CONTACTS: "Customers › Contacts",
  CUSTOMERS_JOBS: "Customers › Jobs",
  CUSTOMER_JOB_DETAILS: "Job details",
  CUSTOMERS_MESSAGES: "Customers › New message",
  CUSTOMER_MESSAGE_DETAILS: "Message details",
  CUSTOMER_STATEMENT_RUNS: "Customers › Statements runs",
  CUSTOMER_STATEMENT_RUN_DETAILS: "Statement run details",

  // Generate
  GENERATE_STATEMENT: "Generate statement",

  /// Vendors
  VENDORS: "Vendors",
  VENDOR_DETAILS: "Vendor details",

  /// Analytics
  ANALYTICS_SUMMARY_V1: "Dashboards › Summary",
  ANALYTICS_SUMMARY_V2: "Dashboards › Summary (New)",
  ANALYTICS_REPORTS: "Reports",
  ANALYTICS_AI: "Dooey",
  ANALYTICS_AI_ROUTINES: "Dooey › Routines",
  ANALYTICS_AI_ROUTINE_DETAILS: "Dooey › Routine Details",
  ANALYTICS_BUILD_REPORT: "Report Builder",

  /// Notifications
  NOTIFICATIONS: "Notifications",
})

export const AdminPageTitles = titles({
  IDEAS: "Ideas",
  INTEGRATIONS_JOURNAL_ENTRIES: "Integrations › Journal entries",
  INTEGRATIONS_JOURNAL_ENTRY_DETAILS: "Journal entry details",
  INTEGRATIONS_SETTINGS: "Integrations › Settings",
  CHART_OF_ACCOUNTS_ACCOUNTS: "Chart of Accounts › Accounts",
  CHART_OF_ACCOUNTS_MAPPING: "Chart of Accounts › Mapping",
  MANUAL_JOURNAL_ENTRIES: "Manual journal entries",
  MANUAL_JOURNAL_ENTRY_DETAILS: "Manual journal entries › Details",
  NEW_MANUAL_JOURNAL_ENTRY: "New manual journal entry",
  EDIT_MANUAL_JOURNAL_ENTRY: "Edit manual journal entry",
  DUPLICATE_MANUAL_JOURNAL_ENTRY: "Duplicate manual journal entry",

  /// Pricing Tiers
  PRICING_TIER_DETAILS: "Pricing tier details",

  /// Accounts
  ACCOUNTS_INTERNAL: "Accounts › Internal",
  ACCOUNTS_EXTERNAL: "Accounts › External",
  ACCOUNTS_EXPRESS: "Accounts › Express",

  /// Reporting
  REPORTING_PROFIT_AND_LOSS: "Reporting › Profit & loss",
  REPORTING_BALANCE_SHEET: "Reporting › Balance sheet",
  REPORTING_PURCHASE_JOURNAL: "Reporting › Purchase journal",
  REPORTING_JOURNAL_ENTRIES: "Reporting › Journal entries",
  REPORTING_PAYOUTS: "Reporting › Payouts",
  REPORTING_GENERAL_LEDGER: "Reporting › General ledger",
  REPORTING_BUILD_REPORT: "Reporting › Report Builder",

  /// General
  COMPANY: "Company",
  LOCATIONS: "Locations",
  LOCATION_DETAILS: "Location details",
  STAFF: "Staff",
  STAFF_DETAILS: "Staff details",

  /// Blocks
  VENDOR: "Vendor",
  CUSTOMER: "Customer",
  PRODUCT: "Product",
  TAGS: "Tags",

  /// Settings
  COMMUNICATIONS: "Communications",
  DOMAINS: "Domains",
  SECURITY: "Security",

  /// Billing
  BILLING: "Billing",

  /// Stripe
  STRIPE: "Stripe",
})

export const AuthPageTitles = titles({
  SIGN_IN: "Sign in",
})

export const ErrorPageTitles = titles({
  ERROR: "Error",
})

type PageTitleObjects = typeof POSPageTitles &
  typeof AdminPageTitles &
  typeof AuthPageTitles &
  typeof ErrorPageTitles

export type HeadTitle = PageTitleObjects[keyof PageTitleObjects]
